import { ENDPOINTS } from "@api/endpoints";
import { handleError } from "@/api";
import axios from "axios";
import { loginSchema } from "@/schemas/auth";
import { buildAuthentikEndSessionUrl } from "@/lib/authentikEndSession";
import { authHelper } from "@/lib/localStore";
import { APP_CONFIG } from "@/config/app";
import { DEFAULT_TENANT_ID } from "@/config/constants";
import { ENV } from "@/config/env";

const {
  AUTH: { LOGIN, REFRESH_TOKEN },
} = ENDPOINTS;

const AUTH_BASE_URL = ENV.AUTH_API_URL || ENV.API_URL;
// const AUTHENTIK_TOKEN_URL = import.meta.env.VITE_AUTHENTIK_TOKEN_URL;
/**
 * Clear all accessible browser storage
 * Clears localStorage, sessionStorage, and non-HttpOnly cookies
 */
export const clearBrowserStorage = () => {
  console.log("[Logout] Clearing browser storage...");

  // Clear localStorage
  try {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.clear();
    console.log("[Logout] localStorage cleared");
  } catch (e) {
    console.error("[Logout] Failed to clear localStorage:", e);
  }

  // Clear sessionStorage
  try {
    sessionStorage.clear();
    console.log("[Logout] sessionStorage cleared");
  } catch (e) {
    console.error("[Logout] Failed to clear sessionStorage:", e);
  }

  // Clear all non-HttpOnly cookies
  document.cookie.split(";").forEach((cookie) => {
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
    if (name) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 2025 00:00:00 UTC;path=/;`;
      document.cookie = `${name}=;expires=Thu, 01 Jan 2025 00:00:00 UTC;path=/;domain=${window.location.hostname};`;
    }
  });
};

const authApi = axios.create({
  baseURL: AUTH_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: APP_CONFIG.API.TIMEOUT,
});

authApi.interceptors.request.use((config) => {
  if (!config.url?.includes("login")) {
    config.headers["X-Tenant-Id"] = DEFAULT_TENANT_ID;
  }
  return config;
});

/**
 * Authentication related API calls
 */
export const authService = {
  /**
   * Login user with email and password
   * @param data - Login data
   * @returns Login response
   * @throws {{ code: string; message: string }} - If the login api fails
   */
  login: async (data: typeof LOGIN.requestType) => {
    try {
      const { success, error } = loginSchema.safeParse(data);
      if (!success) {
        return {
          status: false,
          error: {
            message: error.message,
            code: "VALIDATION_ERROR",
          },
        };
      }
      const response = await authApi.post<typeof LOGIN.responseType>(
        LOGIN.path,
        data,
      );
      return {
        status: true,
        data: response.data,
      };
    } catch (error) {
      throw handleError(error);
    }
  },
  // Add other auth-related API calls...
  /**
   * Refresh token
   * @param data - Refresh token data
   * @returns Refresh token response
   * @throws {{ code: string; message: string }} - If the refresh token api fails
   */
  refreshToken: async (data: typeof REFRESH_TOKEN.requestType) => {
    try {
      const response = await authApi.post<typeof REFRESH_TOKEN.responseType>(
        REFRESH_TOKEN.path,
        { refreshToken: data.refreshToken },
      );

      const payload = response.data as
        | typeof REFRESH_TOKEN.responseType
        | { access_token?: string; refresh_token?: string };
      const oauthPayload = payload as {
        access_token?: string;
        refresh_token?: string;
      };

      const accessToken =
        "token" in payload && typeof payload.token === "string"
          ? payload.token
          : oauthPayload.access_token;

      const nextRefreshToken =
        "refreshToken" in payload && typeof payload.refreshToken === "string"
          ? payload.refreshToken
          : oauthPayload.refresh_token;

      if (!accessToken) {
        throw new Error("No access token in refresh response");
      }

      authHelper.token = accessToken;
      if (nextRefreshToken) {
        authHelper.refreshToken = nextRefreshToken;
      }

      return { status: true };
    } catch (error) {
      throw handleError(error);
    }
  },
  /**
   * Logout user
   * @returns Logout response
   */
  logout: async () => {
    console.log("[Logout] Starting logout process...");
    try {
      const logoutUrl = buildAuthentikEndSessionUrl();
      if (!logoutUrl) {
        console.error(
          "[Logout] Missing end-session URL or VITE_AUTHENTIK_CLIENT_ID",
        );
        return {
          status: false,
        };
      }

      // In a browser SPA, token revocation via cross-origin XHR will often fail due to CORS.
      // The reliable way to end the SSO session is a top-level redirect to Authentik end-session.
      clearBrowserStorage();

      window.location.assign(logoutUrl.toString());

      return {
        status: true,
        redirected: true as const,
      };
    } catch (error) {
      console.error("[Logout] Error during logout:", error);
      // Clear storage even on error to ensure cleanup
      clearBrowserStorage();
      return {
        status: false,
      };
    }
  },
};
